/**
 * SummarySection Component
 * 입출금 합계 표시 섹션
 */
import React, { useMemo } from 'react';
import { HeadWithdrawHistory, HeadGetWithdrawableAmountResponseDto } from '../types/headWithdraw.types';
import { formatAmount } from '../lib/amountFormatterHelpers';
import { getItem } from '@/shared/utils/storage';

interface SummarySectionProps {
    /** 전체 입출금 내역 목록 */
    histories: HeadWithdrawHistory[];
    /** 예치금 잔액 */
    withdrawableAmount: HeadGetWithdrawableAmountResponseDto | null;
}

const SummarySection: React.FC<SummarySectionProps> = ({ histories, withdrawableAmount }) => {
    /** 합계 계산 */
    const summary = useMemo(() => {
        let depositTotal = 0; // 입금 합계 (D만)
        let withdrawalTotal = 0; // 출금 합계 (W만)
        let feeTotal = 0; // 수수료 합계

        histories.forEach((history) => {
            // 입금(D)만 입금 합계에 포함
            if (history.acctType === 'D') {
                depositTotal += Math.abs(history.amount);
            }
            
            // 출금(W)만 출금 합계에 포함
            if (history.acctType === 'W') {
                withdrawalTotal += Math.abs(history.amount);
            }
            
            // 수수료 합계 계산
            if (history.feeAmt) {
                feeTotal += history.feeAmt;
            }
        });

        const netTotal = depositTotal - withdrawalTotal; // 순합계 (입금 - 출금)

        return {
            depositTotal,
            withdrawalTotal,
            feeTotal,
            netTotal
        };
    }, [histories]);

    const balanceAmount = withdrawableAmount?.balAmt || 0;

    // 로컬스토리지에서 ADMIN_YN 값 확인
    const adminYn = getItem<string | boolean>('ADMIN_YN');
    const isAdminYn = adminYn === true || adminYn === 'true' || adminYn === 'Y';

    return (
        <div className="card">
            <div className="card-body">
                <div className="summary-container">
                    {isAdminYn && (
                        <div className="summary-item">
                            <span className="summary-label">예치금 잔액</span>
                            <span className="summary-amount">
                                {formatAmount(balanceAmount)}
                            </span>
                        </div>
                    )}
                    <div className="summary-item">
                        <span className="summary-label">입금 합계</span>
                        <span className="summary-amount amount-deposit">
                            +{formatAmount(summary.depositTotal)}
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">출금 합계</span>
                        <span className="summary-amount amount-withdrawal">
                            -{formatAmount(summary.withdrawalTotal)}
                        </span>
                    </div>
                    <div className="summary-item summary-total">
                        <span className="summary-label">순합계</span>
                        <span className={`summary-amount ${summary.netTotal >= 0 ? 'amount-deposit' : 'amount-withdrawal'}`}>
                            {summary.netTotal >= 0 ? '+' : ''}{formatAmount(summary.netTotal)}
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">수수료 합계</span>
                        <span className="summary-amount">
                            {formatAmount(summary.feeTotal)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummarySection;
